import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { useIsCompactCustomizeBar } from '@/hooks/useIsBreakpoint';
import { Button, Flex, Icon, Text } from '@/ui';
import { useOnboardingAnchor } from '../PlannerOnboarding/anchorContext';
import { AddViewTopBar } from './AddView/AddViewTopBar';
import { useAddViewFlow } from './AddView/useAddViewFlow';
import { BackToAllButton } from './BackToAllButton';
import type { CustomizeControlId } from './customizeOptions';
import { useCustomizeMode } from './useCustomizeMode';
import { useCustomizeOptions } from './useCustomizeOptions';

type TabButtonProps = {
  id: CustomizeControlId;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  onSelect: () => void;
};

const TabButton = ({ id, label, icon, isActive, onSelect }: TabButtonProps) => {
  const anchorRef = useOnboardingAnchor(`customizeTab:${id}`);

  return (
    <Button
      data-active={isActive || undefined}
      ref={anchorRef}
      size="ah-sm"
      variant="ahTab"
      leftIcon={
        <Icon
          as={icon}
          boxSize={4}
          color={isActive ? 'white' : 'magicPurple.500'}
        />
      }
      onClick={onSelect}
    >
      {label}
    </Button>
  );
};

export const CustomizeBar = () => {
  const {
    activeControlId,
    exitCustomizeMode,
    goToCustomizeHome,
    setActiveControl,
  } = useCustomizeMode();
  const { experience } = usePageExperienceContext();
  const addViewFlow = useAddViewFlow();
  const customizeOptions = useCustomizeOptions();
  const isCompact = useIsCompactCustomizeBar();

  // While the Add-a-view / Change-layout picker is open, its header + actions
  // take over the whole bar in place of the customize tabs and Done button.
  if (addViewFlow) {
    return <AddViewTopBar {...addViewFlow} />;
  }

  return (
    <Flex
      align="center"
      bg="surface.panel"
      borderBottom="1px solid"
      borderColor="line.panel"
      gap={1}
      h="ah-bar"
      px={4}
      sx={printVisibility.hide()}
    >
      {/* The home screen is the tab rail's own landing state, so it shows no tabs —
          just the back affordance out of whichever panel the user opened. */}
      {activeControlId !== null && (
        <>
          <BackToAllButton onClick={goToCustomizeHome} />
          {/* flexShrink={0} so the tabs keep their labels — the title beside them
              is the flexible element, and below ~1000px it drops entirely. */}
          <Flex
            align="center"
            flexShrink={0}
            gap={1}
            layerStyle="ahTabRail"
            px={1}
            py={1}
          >
            {customizeOptions.map(({ id, label, icon }) => (
              <TabButton
                icon={icon as React.ElementType}
                id={id}
                isActive={activeControlId === id}
                key={id}
                label={label}
                onSelect={() => setActiveControl(id)}
              />
            ))}
          </Flex>
        </>
      )}

      {/* Title and the "Customizing" status are the bar's two decorative
          elements, so they're what a crowded bar gives up first: the purple
          active tab and Done already say the mode is on. */}
      {experience?.title && !isCompact && (
        <Text minW={0} ml={2} noOfLines={1} textStyle="ahControl">
          {experience.title}
        </Text>
      )}

      <Flex align="center" flexShrink={0} gap={3} ml="auto">
        {!isCompact && (
          <Flex align="center" gap={1.5}>
            <Icon color="magicPurple.600" icon="PenTool" size={4} />
            <Text
              color="magicPurple.700"
              fontSize="ah-status"
              fontWeight={700}
              letterSpacing="-0.01em"
            >
              Customizing
            </Text>
          </Flex>
        )}
        <Button
          leftIcon={<Icon icon="Checkmark" size={4} />}
          px={4}
          size="ah"
          variant="ahPrimary"
          onClick={exitCustomizeMode}
        >
          Done
        </Button>
      </Flex>
    </Flex>
  );
};
